/**
 * TrialExpiredBanner Component
 *
 * Shows a prominent banner when trial has expired, explaining
 * read-only mode limitations and prompting upgrade.
 *
 * WP-BILLING-2: Phase 3 - Trial Expiration UI
 */
import { Link } from 'react-router-dom';

export interface TrialExpiredBannerProps {
  /** Optional callback when upgrade button is clicked */
  onUpgradeClick?: () => void;
}

/**
 * Banner displayed at the top of the layout for expired trial users.
 * Uses soft pink styling to prompt upgrade.
 */
export default function TrialExpiredBanner({ onUpgradeClick }: TrialExpiredBannerProps) {
  return (
    <div className="bg-pink-50 border-b border-pink-200" role="alert">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Icon and message */}
          <div className="flex items-center gap-3">
            <ExclamationIcon className="h-5 w-5 flex-shrink-0 text-pink-400" />
            <p className="text-sm font-medium text-pink-800">
              <span className="font-semibold">Your trial has expired.</span>
              <span className="hidden sm:inline">
                {' '}Your account is now in read-only mode. Upgrade to continue
                adding accounts, syncing data, and using premium features.
              </span>
              <span className="sm:hidden">
                {' '}Upgrade to restore full access.
              </span>
            </p>
          </div>

          {/* CTA button */}
          <Link
            to="/billing"
            onClick={onUpgradeClick}
            className="
              inline-flex items-center px-4 py-2 rounded-md text-sm font-medium
              text-white shadow-sm transition-colors
              bg-pink-600 hover:bg-pink-700
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500
            "
          >
            Upgrade Now
          </Link>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function ExclamationIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}
